/**
 * TrainersPage: Trainer-/Coach-Verwaltung.
 *
 * Specialties als komma-getrennte Liste im Input, beim Speichern als Array.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiCallError, type Trainer } from "../lib/api";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Label } from "../components/Label";
import { Drawer } from "../components/Drawer";
import { Badge } from "../components/Badge";
import { ListToolbar } from "../components/ListToolbar";
import { ActiveToggle } from "../components/ActiveToggle";
import { useAuth } from "../lib/auth";
import { t } from "../locales/de";

type FormState = {
  fullName: string;
  email: string;
  phone: string;
  specialtiesText: string;
  active: boolean;
};

function toForm(tr: Trainer): FormState {
  return {
    fullName: tr.fullName,
    email: tr.email ?? "",
    phone: tr.phone ?? "",
    specialtiesText: tr.specialties.join(", "),
    active: tr.isActive,
  };
}

const EMPTY: FormState = { fullName: "", email: "", phone: "", specialtiesText: "", active: true };

export function TrainersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filterQ, setFilterQ] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Trainer | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const canWrite = user?.role === "admin" || user?.role === "planer";
  const canDelete = user?.role === "admin";

  const trQ = useQuery({
    queryKey: ["trainers", filterQ],
    queryFn: () => api.listTrainers(filterQ || undefined),
  });
  const trainers = trQ.data?.trainers ?? [];

  const createMut = useMutation({
    mutationFn: (body: Partial<Trainer> & { specialties?: string[] }) => api.createTrainer(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trainers"] }); setDrawerOpen(false); },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Trainer> & { specialties?: string[] } }) =>
      api.updateTrainer(id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["trainers"] }); setDrawerOpen(false); },
    onError: (e) => setError(e instanceof ApiCallError ? e.message : "Fehler"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteTrainer(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trainers"] }),
    onError: (e) => alert(e instanceof ApiCallError ? e.message : "Löschen fehlgeschlagen"),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setError(null); setDrawerOpen(true); }
  function openEdit(tr: Trainer) { setEditing(tr); setForm(toForm(tr)); setError(null); setDrawerOpen(true); }
  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const specialties = form.specialtiesText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const body = {
      fullName: form.fullName.trim(),
      email: editing ? form.email.trim() : form.email.trim() || undefined,
      phone: editing ? form.phone.trim() : form.phone.trim() || undefined,
      specialties,
      active: form.active,
    };
    if (!body.fullName) return setError("Name ist erforderlich");
    if (editing) updateMut.mutate({ id: editing.id, body });
    else createMut.mutate(body);
  }

  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t.nav.trainers}</h1>
        {canWrite && <Button onClick={openCreate}>+ Neuer Trainer</Button>}
      </div>

      <ListToolbar searchValue={filterQ} onSearchChange={setFilterQ} searchPlaceholder="Name oder E-Mail suchen…" resultCount={trainers.length} hasActiveFilters={Boolean(filterQ)} onReset={() => setFilterQ("")} />

      <div className="rounded-lg border border-slate-200 bg-white">
        {trQ.isLoading ? (
          <div className="p-6 text-sm text-slate-500">Lade…</div>
        ) : trQ.isError ? (
          <div className="p-6 text-sm text-rose-700">Trainer konnten nicht geladen werden. Bitte Seite neu laden.</div>
        ) : trainers.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Keine Trainer.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">E-Mail</th>
                <th className="px-4 py-3">Telefon</th>
                <th className="px-4 py-3">Specialties</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {trainers.map((tr) => (
                <tr key={tr.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-slate-900">{tr.fullName}</td>
                  <td className="px-4 py-3 text-slate-600">{tr.email ?? "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{tr.phone ?? "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {tr.specialties.length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        tr.specialties.map((s) => <Badge key={s} variant="info">{s}</Badge>)
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {tr.isActive ? <Badge variant="success">Aktiv</Badge> : <Badge variant="neutral">Inaktiv</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {canWrite && (
                        <ActiveToggle
                          checked={tr.isActive}
                          disabled={updateMut.isPending}
                          label={`Trainer ${tr.fullName}`}
                          onChange={() => updateMut.mutate({ id: tr.id, body: { isActive: !tr.isActive } })}
                        />
                      )}
                      {canWrite && <Button variant="secondary" onClick={() => openEdit(tr)}>Bearbeiten</Button>}
                      {canDelete && (
                        <Button variant="danger" onClick={() => {
                          if (confirm(`Trainer "${tr.fullName}" wirklich löschen?`)) deleteMut.mutate(tr.id);
                        }}>Löschen</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Trainer bearbeiten" : "Neuer Trainer"}>
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
          <div>
            <Label htmlFor="fullName">Name *</Label>
            <Input id="fullName" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
          </div>
          <div>
            <Label htmlFor="email">E-Mail</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="phone">Telefon</Label>
            <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="spec">Specialties (komma-getrennt)</Label>
            <Input id="spec" value={form.specialtiesText} onChange={(e) => setForm({ ...form, specialtiesText: e.target.value })} placeholder="z. B. Java, TypeScript, Agile" />
          </div>
          <div className="flex items-center gap-2">
            <input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
            <Label htmlFor="active" className="!mb-0">Aktiv</Label>
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
            <Button variant="secondary" type="button" onClick={() => setDrawerOpen(false)}>Abbrechen</Button>
            <Button type="submit" disabled={submitting}>{submitting ? "Speichere…" : editing ? "Speichern" : "Anlegen"}</Button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}
